import type { Ref } from 'react';
import styles from './SuggestionPill.module.css';

/**
 * Figma: `Suggestion Pill` (2071:38764) on the AI Blocks page — one of the
 * prompts DeepDive AI offers under its empty composer. A rounded, bordered
 * chip of one line of text.
 *
 * `State=Default|Hover` and a `Text` property, so the API is a label, a
 * handler and the usual forced state for documentation.
 *
 * It is a `<button>`, not a chip: pressing it does something. The design draws
 * no pressed or focus state, so both are additions — the press dip every other
 * control in the system has, and a focus ring, since a keyboard user has to be
 * able to reach a prompt that the mouse can take in one click.
 */
export type SuggestionPillState = 'default' | 'hover';

export type SuggestionPillProps = {
  /** Figma's `Prompt` text — the prompt offered, shown in full on one line. */
  label: string;
  /** Receives `label`. The DeepDive screens put it in the composer rather than sending it. */
  onSelect?: (label: string) => void;
  /** Force a state for documentation and visual regression only. */
  state?: SuggestionPillState;
  className?: string;
  ref?: Ref<HTMLButtonElement>;
};

export function SuggestionPill({ label, onSelect, state, className, ref }: SuggestionPillProps) {
  const classes = [
    styles.pill,
    state === 'hover' ? styles.hover : null,
    // Live hover only when no state is forced, so `:hover` reacts in the app and
    // stays inert in a states story.
    state === undefined ? styles.interactive : null,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <button type="button" className={classes} onClick={() => onSelect?.(label)} ref={ref}>
      {label}
    </button>
  );
}
